import React, { useCallback, useMemo, useState } from "react"
import { LayoutChangeEvent, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import styled from "styled-components/native"

import { simpleHash } from "@treefort/lib/simple-hash"

import { kebabCase } from "../lib/kebab-case"
import { getAbsoluteLineHeight } from "../lib/text-style"
import ListView from "./list-view"
import Modal, { MODAL_HEADER_HEIGHT_PX } from "./modal"
import Row from "./row"
import Text from "./text"
import { useTokens } from "./tokens-provider"
import Touchable from "./touchable"

export type Option<T> = { label: string; value: T; depth?: number }

type Numbering = { index: number; optionCount: number }

const OPTION_LABEL_TEXT_STYLE = "headingSmall"

const OPTION_PADDING_VERTICAL_PX = 12

const SELECTED_DOT_SIZE_PX = 8

const INDENTATION_PX = 24

// Unlocks a scroll view during body scroll locking on the web
const WEB_SCROLL_UNLOCK = { webScrollUnlock: true }

const OptionLabel = styled(Text)<{
  $numbering: Numbering | undefined
  $depth: number
  $selected: boolean | undefined
}>`
  margin-left: ${(props) =>
    props.$numbering
      ? 0
      : props.$depth * INDENTATION_PX +
        (!props.$selected ? SELECTED_DOT_SIZE_PX * 2 : 0)}px;
`

const SelectedDot = styled.View`
  width: ${SELECTED_DOT_SIZE_PX}px;
  height: ${SELECTED_DOT_SIZE_PX}px;
  border-radius: ${SELECTED_DOT_SIZE_PX}px;
  background-color: ${(props) => props.theme.colors.accent};
`

const MeasureView = styled.View`
  position: absolute;
  top: ${MODAL_HEADER_HEIGHT_PX}px;
  bottom: 0;
  left: 0;
  right: 0;
`

/**
 * This creates a consistent-width column for option numbers so that option
 * labels have consistent indentation regardless of whether they are numbered
 * with 1, 11, 111, or 1111. Each digit is given 12px which is very
 * un-scientific, but works and includes wiggle room for font variations.
 */
const OptionNumber = styled.View<{
  $numbering: Numbering | undefined
  $depth: number
  $selected: boolean | undefined
}>`
  width: ${(props) =>
    (props.$numbering?.optionCount || "").toString().length * 12}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: ${(props) => props.$depth * INDENTATION_PX}px;
`

export function SelectOption<T>({
  selectId,
  option,
  numbering,
  selected,
  onPress,
  ...rowProps
}: {
  selectId: string
  option: Option<T>
  numbering?: Numbering
  selected?: boolean
  onPress?: (value: T) => void
} & Omit<Parameters<typeof Row>[0], "children" | "gap">) {
  const depth = option.depth || 0
  const item = (
    <Row {...rowProps} gap="xsmall">
      {numbering ? (
        <OptionNumber
          $numbering={numbering}
          $depth={depth}
          $selected={selected}
        >
          {selected ? (
            <SelectedDot />
          ) : (
            <Text textStyle="body" color="secondary">
              {numbering.index + 1}
            </Text>
          )}
        </OptionNumber>
      ) : selected ? (
        <SelectedDot />
      ) : null}
      <OptionLabel
        textStyle={OPTION_LABEL_TEXT_STYLE}
        numberOfLines={1}
        $numbering={numbering}
        $depth={depth}
        $selected={selected}
        color={selected ? "accent" : undefined}
      >
        {option.label}
      </OptionLabel>
    </Row>
  )

  return onPress ? (
    <Touchable
      id={kebabCase(
        `select-opt${`-${selectId}-${option.label || "no-label"}`}`,
      )}
      onPress={() => onPress(option.value)}
      feedback="ripple-or-highlight"
      role="button"
      aria-label={option.label}
    >
      {item}
    </Touchable>
  ) : (
    item
  )
}

export default function Select<T>({
  id,
  open,
  options,
  value,
  label,
  numbered,
  maxWidth,
  onChange,
  onClose,
}: {
  id: string
  open: boolean
  options: Option<T>[]
  value?: T
  label?: string
  numbered?: boolean
  maxWidth?: number | undefined
  onChange?: (value: T) => void
  onClose: () => void
}): React.JSX.Element {
  const { tokens } = useTokens()
  const [viewSize, setViewSize] = useState<{ width: number; height: number }>()
  const safeAreaInsets = useSafeAreaInsets()
  const handleLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setViewSize({
        width: event.nativeEvent.layout.width,
        height: event.nativeEvent.layout.height,
      }),
    [],
  )
  const optionHeight = useMemo(
    () =>
      getAbsoluteLineHeight(OPTION_LABEL_TEXT_STYLE, tokens) +
      OPTION_PADDING_VERTICAL_PX * 2,
    [tokens],
  )
  const selectedIndex = options.findIndex((o) => o.value === value)
  return (
    <Modal
      title={label}
      open={open}
      onPressCloseButton={onClose}
      onPressOutside={onClose}
      type="sheet"
      backgroundColor="tertiary"
      portalHost="foreground"
      maxWidth={maxWidth}
    >
      {(modalType) => (
        <>
          <MeasureView onLayout={handleLayout} />
          {viewSize ? (
            <ListView
              id={id}
              items={options}
              viewSize={viewSize}
              style={[
                viewSize,
                { position: "absolute", top: MODAL_HEADER_HEIGHT_PX, left: 0 },
              ]}
              getItemKey={(option) =>
                typeof option.value === "string"
                  ? option.value
                  : typeof option.value === "number"
                    ? option.value.toString()
                    : simpleHash(option)
              }
              getItemSize={() => optionHeight}
              initialOffset={
                selectedIndex * optionHeight > viewSize.height - optionHeight
                  ? selectedIndex * optionHeight
                  : undefined
              }
              scrollEventThrottle={8}
              scrollViewDataSet={WEB_SCROLL_UNLOCK}
              paddingEnd={
                modalType === "sheet" ? safeAreaInsets.bottom : undefined
              }
              renderItem={(option, index) => (
                <SelectOption
                  selectId={id}
                  option={option}
                  selected={option.value === value}
                  height={optionHeight}
                  onPress={onChange}
                  numbering={
                    numbered
                      ? { index, optionCount: options.length }
                      : undefined
                  }
                  paddingHorizontal="medium"
                />
              )}
            />
          ) : null}
          <View
            style={{
              // This View is used to reserve the space that would normally be
              // taken up by the tracks in the absolutely positioned ListView.
              // This ensures that the modal expands vertically as necessary to
              // accomodate larger numbers of tracks.
              height: options.length * optionHeight,
            }}
            pointerEvents="none"
          />
        </>
      )}
    </Modal>
  )
}
